// CustomerRewardBalanceReport.jsx — "Show Available Smart Points" drill-down from Total Customers.
// Balance per VPA (rewardBalance) + smart point history for the date range (rewardTransactions),
// with the bill amount and payment mode of the game card behind each reward.

import React, { useState, useEffect } from 'react';
import {
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Chip,
} from '@mui/material';
import StarsIcon from '@mui/icons-material/Stars';
import PrintIcon from '@mui/icons-material/Print';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import LocalAtmIcon from '@mui/icons-material/LocalAtm';
import { format, subDays } from 'date-fns';
import axios from 'axios';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { useParams } from 'react-router-dom';
import { API_BASE_URL } from '../../constants/constants';
import PaymentModeChip from '../../components/PaymentModeChip';
import { paymentModeLabel, totalsByModeFromRows } from '../../utils/paymentMode';
import CustomerReportShell, {
  DateRangeFilter,
  ReportGrid,
  SectionCard,
  displayVpa,
  formatINR,
  formatISTDateTime,
  parseApiDate,
  useCanViewCustomerSensitive,
  validateDateRange,
} from '../../components/CustomerReportShell';

const isDebitAction = (action, description) =>
  action === 'manual_from_app' || action === 'reward_withdraw' || description === 'Withdrawal';

const CustomerRewardBalanceReport = () => {
  const [startDate, setStartDate] = useState(subDays(new Date(), 30)); // Default to 30 days before current date
  const [endDate, setEndDate] = useState(new Date()); // Default to current date
  const [balances, setBalances] = useState([]);
  const [grandTotal, setGrandTotal] = useState(0);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [error, setError] = useState('');
  const [dialogMessage, setDialogMessage] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const canViewSensitive = useCanViewCustomerSensitive();

  const { customerId } = useParams();

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, startDate, endDate]);

  const fetchAll = () => {
    const rangeError = validateDateRange(startDate, endDate);
    if (rangeError) {
      setDialogMessage(rangeError);
      setOpenDialog(true);
      return;
    }
    setError('');
    fetchRewardBalanceData();
    fetchHistory();
  };

  const requestBody = () => ({
    customerId,
    startDate: format(startDate, 'yyyy-MM-dd'),
    endDate: format(endDate, 'yyyy-MM-dd'),
    page: 1,
    limit: 1000,
  });

  const authHeaders = () => ({
    headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}`, 'Content-Type': 'application/json' },
  });

  const fetchRewardBalanceData = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API_BASE_URL}/customerReport/rewardBalance`, requestBody(), authHeaders());
      setCustomerName(data.customerName || '');
      setBalances((data.balances || []).map((item, index) => ({
        id: index + 1,
        serialNumber: index + 1,
        customerName: data.customerName || '',
        vpa: item.vpa,
        totalAmount: parseFloat(item.totalAmount || 0),
      })));
      setGrandTotal(parseFloat(data.grandTotal || 0));
    } catch (err) {
      console.error('Error fetching data:', err);
      setError((err.response && err.response.data && err.response.data.message) || 'An error occurred while fetching smart point balance.');
      setBalances([]);
      setGrandTotal(0);
    }
    setLoading(false);
  };

  const fetchHistory = async () => {
    setHistoryLoading(true);
    try {
      const { data } = await axios.post(`${API_BASE_URL}/customerReport/rewardTransactions`, requestBody(), authHeaders());
      setHistory((data.transactions || []).map((item, index) => {
        const debit = isDebitAction(item.action, item.description);
        const hasBill = typeof item.billAmount === 'number';
        return {
          id: String(item.transactionId || index),
          serialNumber: index + 1,
          rawDate: item.createdAt || item.dateTime,
          type: debit ? 'Debit' : 'Credit',
          note: item.description || '-',
          amount: parseFloat(item.amount || 0),
          billAmount: debit || !hasBill ? null : item.billAmount,
          // Mode of pay: from the game card behind the reward; unknown for withdrawals / unmatched rows
          payMode: debit ? null : (item.billPaymentMode || (hasBill ? item.paymentMode : null)),
          paymentMode: item.billPaymentMode || item.paymentMode,
          vpa: item.vpa,
          transactionId: item.transactionId || '',
        };
      }));
    } catch (err) {
      console.error('Error fetching smart point history:', err);
      setError((err.response && err.response.data && err.response.data.message) || 'An error occurred while fetching smart point history.');
      setHistory([]);
    }
    setHistoryLoading(false);
  };

  const credits = history.filter((h) => h.type === 'Credit');
  const debits = history.filter((h) => h.type === 'Debit');
  const earned = credits.reduce((s, h) => s + h.amount, 0);
  const used = debits.reduce((s, h) => s + h.amount, 0);
  const earnedByMode = totalsByModeFromRows(credits, (h) => h.amount);

  const handlePrint = () => {
    if (balances.length === 0 && history.length === 0) {
      setDialogMessage('No data to print.');
      setOpenDialog(true);
      return;
    }
    const doc = new jsPDF('p', 'pt', 'a4');
    const center = doc.internal.pageSize.getWidth() / 2;
    doc.setFontSize(15);
    doc.text('Customer Smart Points Report', center, 40, { align: 'center' });
    doc.setFontSize(10);
    if (customerName) doc.text(`Customer: ${customerName}`, center, 60, { align: 'center' });
    doc.text(`From ${format(startDate, 'dd-MM-yyyy')} to ${format(endDate, 'dd-MM-yyyy')}`, center, customerName ? 76 : 60, { align: 'center' });

    doc.autoTable({
      startY: customerName ? 90 : 74,
      head: [['S.No.', 'Customer', 'VPA', 'Balance']],
      body: balances.map((b) => [b.serialNumber, b.customerName, displayVpa(b.vpa, canViewSensitive), `Rs.${b.totalAmount.toFixed(2)}`]),
      foot: [['', '', 'Grand Total', `Rs.${grandTotal.toFixed(2)}`]],
      theme: 'grid',
      headStyles: { fillColor: [240, 240, 240], textColor: [0, 0, 0], fontStyle: 'bold' },
      footStyles: { fillColor: [255, 255, 255], textColor: [0, 0, 0], fontStyle: 'bold' },
      styles: { fontSize: 10 },
      margin: { right: 30, left: 30 },
    });

    if (history.length) {
      doc.autoTable({
        startY: doc.lastAutoTable.finalY + 24,
        head: [['S.No.', 'Date & Time', 'Type', 'Points', 'Bill Amount', 'Mode of Pay', 'Note']],
        body: history.map((h) => [
          h.serialNumber,
          formatISTDateTime(h.rawDate),
          h.type,
          `Rs.${h.amount.toFixed(2)}`,
          h.billAmount === null ? '-' : `Rs.${Number(h.billAmount).toFixed(2)}`,
          h.payMode ? paymentModeLabel(h.payMode) : '-',
          h.note,
        ]),
        theme: 'grid',
        headStyles: { fillColor: [240, 240, 240], textColor: [0, 0, 0], fontStyle: 'bold' },
        styles: { fontSize: 9 },
        margin: { right: 30, left: 30 },
      });
    }
    doc.save('Customer_Smart_Points_Report.pdf');
  };

  const balanceColumns = [
    { field: 'serialNumber', headerName: '#', width: 60 },
    { field: 'vpa', headerName: 'VPA', flex: 1, minWidth: 200, valueGetter: (v) => displayVpa(v, canViewSensitive) },
    {
      field: 'totalAmount', headerName: 'Balance', width: 160, type: 'number',
      renderCell: (p) => <Typography variant="body2" sx={{ fontWeight: 800, width: '100%', textAlign: 'right' }}>{formatINR(p.value)}</Typography>,
    },
  ];

  const historyColumns = [
    { field: 'serialNumber', headerName: '#', width: 60 },
    { field: 'rawDate', headerName: 'Date & Time (IST)', width: 190, type: 'dateTime',
      valueGetter: (v) => parseApiDate(v), valueFormatter: (v) => formatISTDateTime(v) },
    {
      field: 'type', headerName: 'Type', width: 110,
      renderCell: (p) => (
        <Chip
          size="small"
          label={p.value}
          color={p.value === 'Credit' ? 'success' : 'default'}
          variant={p.value === 'Credit' ? 'filled' : 'outlined'}
          sx={{ fontWeight: 700 }}
        />
      ),
    },
    {
      field: 'amount', headerName: 'Smart Points', width: 140, type: 'number',
      renderCell: (p) => (
        <Typography variant="body2" sx={{ fontWeight: 800, width: '100%', textAlign: 'right', color: p.row.type === 'Credit' ? 'success.dark' : 'error.main' }}>
          {p.row.type === 'Credit' ? '+ ' : '- '}{formatINR(p.value)}
        </Typography>
      ),
    },
    { field: 'billAmount', headerName: 'Bill Amount', width: 140, type: 'number', valueFormatter: (v) => (v === null || v === undefined ? '-' : formatINR(v)) },
    {
      field: 'payMode', headerName: 'Mode of Pay', width: 120,
      renderCell: (p) => (p.value ? <PaymentModeChip mode={p.value} /> : <Typography variant="body2" color="text.secondary">-</Typography>),
    },
    { field: 'note', headerName: 'Description', width: 150 },
    { field: 'vpa', headerName: 'VPA', flex: 1, minWidth: 180, valueGetter: (v) => displayVpa(v, canViewSensitive) },
    {
      field: 'transactionId', headerName: 'Transaction ID', width: 220,
      renderCell: (p) => <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{String(p.value || '-')}</Typography>,
    },
  ];

  return (
    <CustomerReportShell
      title="Smart Points"
      subtitle={`Available balance and smart point history · ${format(startDate, 'dd MMM yyyy')} to ${format(endDate, 'dd MMM yyyy')}`}
      icon={<StarsIcon />}
      customerId={customerId}
      customerName={customerName}
      actions={(
        <Button variant="contained" startIcon={<PrintIcon />} onClick={handlePrint} disabled={balances.length === 0 && history.length === 0} sx={{ fontWeight: 700 }}>
          Print Report
        </Button>
      )}
      filters={(
        <DateRangeFilter startDate={startDate} endDate={endDate} onStartChange={setStartDate} onEndChange={setEndDate} />
      )}
      error={error}
      onRetry={fetchAll}
      statsLoading={loading || historyLoading}
      stats={[
        { label: 'Available Balance', value: formatINR(grandTotal), hint: `${balances.length} VPA wallet${balances.length === 1 ? '' : 's'}`, color: 'primary', icon: <StarsIcon /> },
        { label: 'Earned in Range', value: formatINR(earned), hint: `${credits.length} credit${credits.length === 1 ? '' : 's'}`, color: 'success', icon: <AddCircleOutlineIcon /> },
        { label: 'Withdrawn in Range', value: formatINR(used), hint: `${debits.length} debit${debits.length === 1 ? '' : 's'}`, color: 'error', icon: <RemoveCircleOutlineIcon /> },
        { label: 'Earned · UPI', value: formatINR(earnedByMode.UPI.amount), hint: `${earnedByMode.UPI.count} credit${earnedByMode.UPI.count === 1 ? '' : 's'}`, color: 'secondary', icon: <QrCode2Icon /> },
        { label: 'Earned · Cash', value: formatINR(earnedByMode.CASH.amount), hint: `${earnedByMode.CASH.count} credit${earnedByMode.CASH.count === 1 ? '' : 's'}`, color: 'warning', icon: <LocalAtmIcon /> },
      ]}
    >
      <Grid container spacing={2.5}>
        <Grid item xs={12}>
          <SectionCard title="Smart Point History" subtitle={`${history.length} record${history.length === 1 ? '' : 's'} in the selected dates`}>
            <ReportGrid
              rows={history}
              columns={historyColumns}
              loading={historyLoading}
              emptyTitle="No smart point activity"
              emptyMessage="No smart points were earned or withdrawn in the selected dates."
            />
          </SectionCard>
        </Grid>
        <Grid item xs={12}>
          <SectionCard title="Balance by VPA" subtitle="Current balance (not limited by dates)">
            <ReportGrid
              rows={balances}
              columns={balanceColumns}
              loading={loading}
              emptyTitle="No smart point balance"
              emptyMessage="This customer has no smart point wallet yet."
            />
          </SectionCard>
        </Grid>
      </Grid>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle sx={{ fontWeight: 700 }}>Alert</DialogTitle>
        <DialogContent><Typography>{dialogMessage}</Typography></DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} variant="contained">OK</Button>
        </DialogActions>
      </Dialog>
    </CustomerReportShell>
  );
};

export default CustomerRewardBalanceReport;
